import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api", // Updated port to match backend
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Prefer explicit `authToken`, fall back to other common keys or userData.token
    let token =
      localStorage.getItem("authToken") ||
      localStorage.getItem("token") ||
      (function () {
        try {
          const u = JSON.parse(localStorage.getItem("userData") || "{}");
          return u?.token || u?.accessToken || null;
        } catch (e) {
          return null;
        }
      })();

    if (token) {
      config.headers.Authorization = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const getAuthHeader = async (explicitToken) => {
  const raw =
    explicitToken ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    (() => {
      try {
        const u = JSON.parse(localStorage.getItem("userData") || "{}");
        return u.token || u.accessToken;
      } catch {
        return "";
      }
    })();
  if (!raw) return {};
  return {
    Authorization: raw.startsWith("Bearer ") ? raw : `Bearer ${raw}`,
  };
};

export const apiRequest = async (method, url, data = {}, config = {}) => {
  // console.log("auth helper")
  try {
    const response = await api({ method, url, data, ...config });
    console.log("response", response);
    // Handle the specific response structure
    if (response.data && typeof response.data === "object") {
      const { status, response_code, message, ...rest } = response.data;

      // If status is false, treat as error
      if (status === false) {
        const error = new Error(message || "Request failed");
        error.response_code = response_code;
        error.status = status;
        error.data = rest;
        throw error;
      }

      // Return the rest of the data if status is true
      // console.log("rest",rest)
      return rest;
    }

    return response.data;
  } catch (error) {
    // If error already has response_code (thrown from try block above), re-throw it
    if (error.response_code !== undefined) {
      throw error;
    }

    // Enhanced error handling for axios errors
    if (error.response?.data) {
      const { status, response_code, message, ...rest } = error.response.data;
      const apiError = new Error(message || "Request failed");
      apiError.response_code = response_code;
      apiError.status = status;
      apiError.data = rest;
      throw apiError;
    }

    // Network or other errors
    const networkError = new Error(error.message || "Network error occurred");
    networkError.isNetworkError = true;
    throw networkError;
  }
};

// Auth-specific API functions
export const authAPI = {
  // Regular email/password login
  login: async (email, password, recaptchaToken) => {
    return await apiRequest("post", "/login", { email, password, recaptchaToken });
  },

  // Regular email/password signup
  signup: async (name, email, password, picture = null, TC = "", recaptchaToken) => {
    const data = { name, email, password, recaptchaToken };
    const is = TC === "" || TC === null;
    if (picture) data.picture = picture;
    return await apiRequest("post", is ? "/signup" : `/signup?TC=${TC}`, data);
  },

  // Google OAuth login
  googleAuth: async (sub, name, email, picture = null) => {
    const data = { sub, name, email };
    if (picture) data.picture = picture;
    return await apiRequest("post", "/auth/google", data);
  },

  // Google OAuth signup
  googleAuthV1: async (sub, name, email, picture = null, TC = "") => {
    const data = { sub, name, email };
    if (picture) data.picture = picture;
    const is = TC === "" || TC === null;
    return await apiRequest("post", is ? "/auth/google-v1" : `/auth/google-v1?TC=${TC}`, data);
  },

  // Forgot password
  forgotPassword: async (email) => {
    return await apiRequest("post", "/forgot-password", { email });
  },

  // Reset password
  resetPassword: async (token, newPassword) => {
    return await apiRequest("post", "/reset-password", { token, newPassword });
  },

  // Change password (for logged-in users)
  changePassword: async (currentPassword, newPassword) => {
    return await apiRequest("post", "/change-password", { currentPassword, newPassword });
  },

  // Resend verification email
  resendVerification: async (email) => {
    return await apiRequest("post", "/resend-verification", { email });
  },
};


// Manager endpoints
export const managerAPI = {
  // GET /api/manager/org/:orgId/users-with-deals
  getUsersWithDealsByOrg: async () => {
    return await apiRequest("get", `/manager/users-with-deals`);
  },
  // GET /api/insights/conversations
  getConversationsOverview: async () => {
    return await apiRequest("get", `/insights/conversations`);
  },
  // GET /api/manager-lead/get-manager-lead
  getManagerLeads: async (params = {}) => {
    // Construct query parameters
    const qs = {};
    if (params.stageOrder) qs.stageOrder = params.stageOrder;
    if (params.startDate) qs.startDate = params.startDate;
    if (params.endDate) qs.endDate = params.endDate;
    if (params.dealName) qs.dealName = params.dealName;
    if (params.assignedPersonName) qs.assignedPersonName = params.assignedPersonName;
    if (params.dateType) qs.dateType = params.dateType;
    if (params.source) qs.source = params.source;

    return await apiRequest("get", "/manager-lead/get-manager-lead", {}, { params: qs });
  },

  // GET /api/manager-lead/get-deals-by-customer?customerId=<id>
  getDealsByCustomer: async (customerId) => {
    return await apiRequest("get", "/manager-lead/get-deals-by-customer", {}, { params: { customerId } });
  },

};


// Helper to safely add userId to query
function safeUserId(userId) {
  if (!userId) return undefined;
  if (typeof userId === "string") return userId;
  if (typeof userId === "object" && userId._id) return userId._id;
  return undefined;
}

export const stageAPI = {
  getStageUserWiseDetail: async (stageId, userId) => {
    const q = typeof stageId === "string" ? encodeURIComponent(stageId) : stageId;
    let url = `/stage-detail/stage-user-wise-detail-v1?stageId=${q}`;
    const uid = safeUserId(userId);
    if (uid) url += `&userId=${uid}`;
    return await apiRequest("get", url);
  },

  getManagerPipelineSummary: async (params = {}, userId) => {
    const qs = { ...(params || {}) };
    if (!qs.scope) qs.scope = "team";
    const uid = safeUserId(userId);
    if (uid) {
      qs.scope = "user";
      qs.userId = uid;
    }
    return await apiRequest("get", `/stage-detail/stage-summary-v1`, {}, { params: qs });
  },

  getTeamPerformanceMetrics: async (arg) => {
    const url = `/stage-detail/team-performance-metrics-v1`;
    // Support two call styles:
    // 1) caller passes a params object: { startDate, endDate, userId }
    // 2) caller passes a userId directly (string or object with _id)
    let config = {};
    if (!arg) {
      // no params
      return await apiRequest("get", url);
    }

    if (typeof arg === "object" && (Array.isArray(Object.keys(arg)) && Object.keys(arg).length > 0)) {
      // treat as params object
      config = { params: arg };
    } else {
      const uid = safeUserId(arg);
      if (uid) config = { params: { userId: uid } };
    }

    return await apiRequest("get", url, {}, config);
  },

  getUserStageItems: async (stageId, userId) => {
    let url = `/stage-detail/user-stage-items-v1?stageId=${stageId}`;
    const uid = safeUserId(userId);
    if (uid) url += `&userId=${uid}`;
    return await apiRequest("get", url);
  },

  getLeadSourceMetrics: async (userId) => {
    const url = `/stage-detail/lead-source-metrics-v1`;
    // Support two call styles:
    // 1) caller passes a params object: { startDate, endDate, userId }
    // 2) caller passes a userId directly (string or object with _id)
    if (!userId) {
      return await apiRequest("get", url);
    }

    if (typeof userId === "object" && Object.keys(userId).length > 0) {
      return await apiRequest("get", url, {}, { params: userId });
    }

    const uid = safeUserId(userId);
    if (uid) return await apiRequest("get", `${url}?userId=${uid}`);

    return await apiRequest("get", url);
  },

  getTeamTaskStats: async (userId) => {
    const url = `/stage-detail/team-task-v1`;
    // Support two call styles:
    // 1) caller passes a params object: { startDate, endDate, userId }
    // 2) caller passes a userId directly (string or object with _id)
    if (!userId) {
      return await apiRequest("get", url);
    }

    if (typeof userId === "object" && Object.keys(userId).length > 0) {
      return await apiRequest("get", url, {}, { params: userId });
    }

    const uid = safeUserId(userId);
    if (uid) return await apiRequest("get", url, {}, { params: { userId: uid } });

    return await apiRequest("get", url);
  },

  // GET /analytics/top-questions
  getTopQuestions: async (params = {}) => {
    return await apiRequest("get", "/analytics/top-questions", {}, { params });
  },

  // GET /tasks/team-stage-deal-count
  getTeamStageDealCount: async (arg) => {
    const url = `/tasks/team-stage-deal-count`;
    // Support calling with params object or a userId
    if (!arg) return await apiRequest("get", url);

    if (typeof arg === "object" && Object.keys(arg).length > 0) {
      return await apiRequest("get", url, {}, { params: arg });
    }

    const uid = safeUserId(arg);
    if (uid) return await apiRequest("get", url, {}, { params: { userId: uid } });

    return await apiRequest("get", url);
  },
};

// // New: Stage detail endpoints used by frontend
// export const stageAPI = {
//   // GET /stage-detail/stage-user-wise-detail-v1?stageId=<id>[&userId=<id>]
//   getStageUserWiseDetail: async (stageId, userId) => {
//     const q = typeof stageId === "string" ? encodeURIComponent(stageId) : stageId;
//     let url = `/stage-detail/stage-user-wise-detail-v1?stageId=${q}`;
//     if (userId) url += `&userId=${userId}`;
//     return await apiRequest("get", url);
//   },

//   // GET /stage-detail/stage-summary-v1?scope=team|user[&userId=<id>]
//   getManagerPipelineSummary: async (params = {}, userId) => {
//     const qs = { ...(params || {}) };
//     if (!qs.scope) qs.scope = "team";
//     if (userId) {
//       qs.scope = "user";
//       qs.userId = userId;
//     }
//     return await apiRequest("get", `/stage-detail/stage-summary-v1`, {}, { params: qs });
//   },

//   // GET /stage-detail/team-performance-metrics-v1[?userId=<id>]
//   getTeamPerformanceMetrics: async (userId) => {
//     let url = `/stage-detail/team-performance-metrics-v1`;
//     if (userId) url += `?userId=${userId}`;
//     return await apiRequest("get", url);
//   },

//   // GET /stage-detail/user-stage-items-v1?stageId=<id>[&userId=<id>]
//   getUserStageItems: async (stageId, userId) => {
//     let url = `/stage-detail/user-stage-items-v1?stageId=${stageId}`;
//     if (userId) url += `&userId=${userId}`;
//     return await apiRequest("get", url);
//   },

//   // GET /stage-detail/lead-source-metrics-v1[?userId=<id>]
//   getLeadSourceMetrics: async (userId) => {
//     let url = `/stage-detail/lead-source-metrics-v1`;
//     if (userId) url += `?userId=${userId}`;
//     return await apiRequest("get", url);
//   },

//   // GET /stage-detail/team-task-v1[?userId=<id>]
//   getTeamTaskStats: async (userId) => {
//     let url = `/stage-detail/team-task-v1`;
//     if (userId) url += `?userId=${userId}`;
//     return await apiRequest("get", url);
//   },
// };

// Team management API functions
export const teamAPI = {
  // Get team members for the current manager
  getTeamMembers: async () => {
    return await apiRequest("get", "/invite/get-team-member");
  },

  // Assign lead to a team member
  assignLead: async (leadId, assignedTo) => {
    return await apiRequest("post", `/facebook/assign-lead/${leadId}`, {
      assignedTo,
    });
  },

  // Assign deal to a team member
  assignDeal: async (dealId, assignedTo) => {
    return await apiRequest("post", `/deal/assign/${dealId}`, {
      assignedTo,
    });
  },
};

// Leads management API functions
export const leadsAPI = {
  // Get leads by user role (manager sees all, member sees assigned)
  // getLeadsByUser: accepts optional `extraParams` e.g. { startDate, endDate }
  getLeadsByUser: async (userId, extraParams = {}) => {
    // Build query string from extraParams if present
    const qs = new URLSearchParams();
    if (extraParams) {
      if (extraParams.startDate) qs.append('startDate', extraParams.startDate);
      if (extraParams.endDate) qs.append('endDate', extraParams.endDate);
    }

    const queryString = qs.toString();

    if (userId) {
      const base = `/facebook/manager-leads/${userId}`;
      const url = queryString ? `${base}?${queryString}` : base;
      return await apiRequest("get", url);
    } else {
      const base = "/facebook/manager-leads";
      const url = queryString ? `${base}?${queryString}` : base;
      return await apiRequest("get", url);
    }
  },
  // Get specific deal info by dealId
  getDealInfo: async (dealId) => {
    return await apiRequest("get", `/deal-info/getDealInfo`, {}, { params: { dealId } });
  },
  // Get deal overview by dealId
  getDealOverview: async (dealId) => {
    return await apiRequest("get", `/deal-info/getDealOverview`, {}, { params: { dealId } });
  },
  // Get deal health by dealId
  getDealHealth: async (dealId) => {
    return await apiRequest("get", `/deal-info/getDealHealth`, {}, { params: { dealId } });
  },
  // Get deal activity by dealId
  getDealActivity: async (dealId) => {
    return await apiRequest("get", `/deal-info/getDealActivity`, {}, { params: { dealId } });
  },
  // Get deal product insights by dealId
  getDealProductInsights: async (dealId) => {
    return await apiRequest("get", `/deal-info/getDealProductInsights`, {}, { params: { dealId } });
  },
  // Get deal AI insights by dealId
  getDealAIInsights: async (dealId) => {
    return await apiRequest("get", `/deal-info/getDealAIInsights`, {}, { params: { dealId } });
  }
};

export const getClientCompane = {
  getClientCompanies: async () => {
    return await apiRequest("get", "/client-companies/get-v1");
  },

  updateCompany: async (id, data) => {
    return await apiRequest(
      "patch",
      `/client-companies/update/${id}`,
      data,

    )
  },
  createCompany: async (data) => {
    return await apiRequest("post", "/client-companies/create", data)
  }
}



// File upload API functions
export const fileAPI = {
  // Generate signed URL for file upload
  generateUploadUrl: async (fileName, fileType, fileSize, type) => {
    return await apiRequest("post", "/upload/file", {
      fileName,
      fileType,
      fileSize,
      type,
    });
  },

  // Save file metadata to database
  saveFileMetadata: async (fileData) => {
    return await apiRequest("post", "/files/metadata", fileData);
  },

  // Get all files for organization
  getOrganizationFiles: async (organizationId) => {
    return await apiRequest("get", `/files/${organizationId}`);
  },

  // Get files by type for organization
  getFilesByType: async (organizationId, type) => {
    return await apiRequest("get", `/files/${organizationId}/${type}`);
  },

  // Delete a file
  deleteFile: async (fileId) => {
    return await apiRequest("delete", `/files/${fileId}`);
  },

  // Upload file to Google Cloud Storage using signed URL
  uploadToGCS: async (signedUrl, file, onProgress) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable && onProgress) {
          const percentComplete = (event.loaded / event.total) * 100;
          onProgress(percentComplete);
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status === 200 || xhr.status === 204) {
          resolve({ success: true });
        } else {
          reject(new Error(`Upload failed with status: ${xhr.status}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Upload failed"));
      });

      xhr.open("PUT", signedUrl);
      xhr.setRequestHeader("Content-Type", file.type);
      xhr.send(file);
    });
  },

  // Upload file to backend (for local storage fallback)
  uploadToBackend: async (file, onProgress) => {
    const formData = new FormData();
    formData.append("file", file);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable && onProgress) {
          const percentComplete = (event.loaded / event.total) * 100;
          onProgress(percentComplete);
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (error) {
            resolve({ success: true });
          }
        } else {
          reject(new Error(`Upload failed with status: ${xhr.status}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Upload failed"));
      });

      // Add authorization header
      const token = localStorage.getItem("authToken");
      if (token) {
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      }

      xhr.open(
        "POST",
        `${process.env.REACT_APP_API_BASE_URL || "http://localhost:8080/api"
        }/upload/local`
      );
      xhr.send(formData);
    });
  },

  // Get pre-call insights for organization
  getPreCallInsights: async (organizationId) => {
    return await apiRequest("get", `/pre-call-insights/${organizationId}`);
  },
};

export default api;


// Dialer specific API functions
export const dialerAPI = {
  // GET /webhook/call/:callSid
  getCallStatus: async (callSid, userId) => {
    // Construct root URL by removing /api from the base URL if present
    const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";
    const rootUrl = baseUrl.replace(/\/api$/, "");
    let url = `${rootUrl}/webhook/call/${callSid}`;
    if (userId) {
      url += `?userId=${userId}`;
    }
    return await apiRequest("get", url);
  },

  // GET /webhook/calls/unread
  getUnreadCalls: async (lastFetchTime, userId) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";
    const rootUrl = baseUrl.replace(/\/api$/, "");
    let url = `${rootUrl}/webhook/calls/unread`; // Updated to plural 'calls' as per user snippet
    const params = [];
    if (lastFetchTime) params.push(`lastFetchTime=${lastFetchTime}`);
    if (userId) params.push(`userId=${userId}`);

    if (params.length > 0) {
      url += `?${params.join("&")}`;
    }
    return await apiRequest("get", url);
  },

  // POST /webhook/call/initiate
  // Reverting to /webhook base as per user confirmation of successful 400 reachability previously.
  initiateCall: async (customerNumber, dealId) => {
    // Construct root URL by removing /api from the base URL if present
    // Endpoint: http://localhost:5000/webhook/call/initiate
    const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";
    const rootUrl = baseUrl.replace(/\/api$/, "");
    const url = `${rootUrl}/webhook/call/initiate`;

    console.log("Initiating call via:", url, { customerNumber, dealId });

    return await apiRequest("post", url, {
      customer_number: customerNumber,
      dealId: dealId
    });
  },
};

// tasks API (placed after other API helpers to keep imports at top)
export const tasksAPI = {
  // GET /tasks/get
  // Accepts optional `extraParams` (e.g. { startDate, endDate }) which will
  // be forwarded as query params. Backend may ignore unknown params, but
  // including them here allows frontend to request filtered data when supported.
  getTasks: async (userId, extraParams = {}) => {
    const qs = {};
    if (userId) {
      const uid = safeUserId(userId);
      if (uid) qs.userId = uid;
    }
    if (extraParams) {
      if (extraParams.startDate) qs.startDate = extraParams.startDate;
      if (extraParams.endDate) qs.endDate = extraParams.endDate;
    }
    return await apiRequest("get", "/tasks/get", {}, { params: qs });
  },
};

export const manageDealsAPI = {
  getDealActivities: async (dealId) => {
    return await apiRequest("get", `/manage-deals/deal-activities/${dealId}`);
  },
  // Phone Number Approval Flow
  updatePhone: async (dealId, phoneNumber, reason) => {
    return await apiRequest("put", `/manage-deals/deals/${dealId}/update-phone`, { phoneNumber, reason });
  },
  getPendingPhoneApprovals: async () => {
    return await apiRequest("get", `/manage-deals/pending-phone-approvals`);
  },
  approvePhoneChange: async (notificationId) => {
    return await apiRequest("post", `/manage-deals/approve-phone-change`, { notificationId });
  },
  rejectPhoneChange: async (notificationId, reason) => {
    return await apiRequest("post", `/manage-deals/reject-phone-change`, { notificationId, reason });
  },
  // Delete Deal (only for admin/manager)
  deleteDeal: async (dealId) => {
    return await apiRequest("patch", `/deal/delete-deal`, {}, { params: { dealId: dealId } });
  }
};

export const dealInfoAPI = {
  // Fetch detailed info for a single deal
  getDealInfo: async (dealId) => {
    return apiRequest('GET', `/deal-info/getDealInfo`, {}, { params: { dealId } });
  }
};

// Plans API functions
export const plansAPI = {
  // Get all active plans
  getPlans: async () => {
    return await apiRequest("get", "/plans/get");
  },

  // Get specific plan by ID
  getPlanById: async (planId) => {
    return await apiRequest("get", `/plans/${planId}`);
  }
};
